import type { TrackId } from './types';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  notFound: {
    id: 'data.load.notFound',
    defaultMessage: 'файл фикстуры не найден',
    description: 'Причина, почему блок пуст (строчная фраза после «…: »): демонстрационный файл не найден на сервере',
  },
  serverStatus: {
    id: 'data.load.serverStatus',
    defaultMessage: 'сервер ответил {status}',
    description: 'Причина, почему блок пуст (строчная фраза): сервер вернул ошибку; {status} — HTTP-код (500)',
  },
  empty: {
    id: 'data.load.empty',
    defaultMessage: 'файл пуст',
    description: 'Причина, почему блок пуст (строчная фраза): файл данных пришёл пустым',
  },
  notJson: {
    id: 'data.load.notJson',
    defaultMessage: 'файл не разбирается как JSON',
    description: 'Причина, почему блок пуст (строчная фраза): файл данных повреждён',
  },
  offline: {
    id: 'data.load.offline',
    defaultMessage: 'сеть недоступна',
    description: 'Причина, почему блок пуст (строчная фраза): нет связи с сервером',
  },
});

/** Корень фикстур. Раздаётся плагином `proto-data` (см. vite.config.ts). */
export const DATA_ROOT = '/data';

export const TRACK_DIR: Record<TrackId, string> = {
  A: 'track-a',
  B: 'track-b',
};

/** Крошечный набор на случай, если выбранный трек ещё не сгенерирован. */
export const STUB_DIR = 'track-stub';

/** Геометрия общая для треков: `prototype/data/geo/contours.geo.json`. */
export const GEO_CONTOURS = `${DATA_ROOT}/geo/contours.geo.json`;
export const GEO_CONTOURS_STUB = `${DATA_ROOT}/${STUB_DIR}/contours.geo.json`;

/**
 * Справочник карантинных видов общий для треков (не генерируется per-track,
 * авторский список без RNG) — лежит в корне фикстур, как и шаблоны выше.
 * Стаб-трек своей копии не держит: файл статичен и не зависит от трека.
 */
export const QUARANTINE_CATALOG_URL = `${DATA_ROOT}/quarantine-catalog.json`;

export type LoadOk<T> = { ok: true; value: T };
export type LoadFail = { ok: false; reason: string };
export type LoadResult<T> = LoadOk<T> | LoadFail;

const cache = new Map<string, Promise<LoadResult<unknown>>>();

/**
 * Загрузка JSON без исключений и без console.error: отсутствующая фикстура —
 * это состояние экрана («данные ещё генерируются»), а не авария.
 */
export function loadJson<T>(url: string): Promise<LoadResult<T>> {
  const hit = cache.get(url);
  if (hit) return hit as Promise<LoadResult<T>>;

  const task: Promise<LoadResult<unknown>> = fetch(url, { headers: { Accept: 'application/json' } })
    .then(async (res): Promise<LoadResult<unknown>> => {
      if (!res.ok) {
        return {
          ok: false,
          reason:
            res.status === 404
              ? tr(m.notFound)
              : tr(m.serverStatus, { status: res.status }),
        };
      }
      const text = await res.text();
      if (!text.trim()) return { ok: false, reason: tr(m.empty) };
      try {
        return { ok: true, value: JSON.parse(text) as unknown };
      } catch {
        return { ok: false, reason: tr(m.notJson) };
      }
    })
    .catch((): LoadResult<unknown> => ({ ok: false, reason: tr(m.offline) }));

  // Неудачи не кэшируются навсегда: транзитный 404 (пересборка dist/data,
  // сеть) не должен прилипать до перезагрузки страницы.
  const guarded = task.then((res) => {
    if (!res.ok) cache.delete(url);
    return res;
  });

  cache.set(url, guarded);
  return guarded as Promise<LoadResult<T>>;
}

/** Сбрасывает кэш (нужен только при смене трека в dev). */
export function clearDataCache(): void {
  cache.clear();
}

/* ------------------------------------------------------------ имена файлов */

export const FILES = {
  meta: 'meta.json',
  /**
   * Шаблоны агроциклов. Общие для треков, поэтому у фикстур лежат в корне
   * (см. `ROOT_SETS` в `data-source/fixtures.ts`), а у канона собираются
   * адаптером из `/agrocycle/templates`. Экран называет НАБОР, а не адрес:
   * иначе перевод экрана на канон был бы правкой экрана.
   */
  templates: 'templates.json',
  massifs: 'massifs.json',
  crops: 'crops.json',
  farms: 'farms.json',
  contours: 'contours.meta.json',
  cycles: 'cycles.json',
  quarantine: 'quarantine.json',
  attention: 'attention.json',
  equipment: 'equipment.json',
  works: (cycleId: string) => `works-${cycleId}.json`,
  obligations: (cycleId: string) => `obligations-${cycleId}.json`,
  water: (cycleId: string) => `water-${cycleId}.json`,
  events: (cycleId: string) => `events-${cycleId}.json`,
  extraWorks: (cycleId: string) => `extra-works-${cycleId}.json`,
} as const;
